// src/app/pages/dashboard/projected-impact/projected-impact.component.ts
import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';

import { AnalysisStore } from '../../../core/shared/t0-model/analysis.store';
import { T0ApiResponse } from '../../../core/shared/t0-model/t0-response-model';

type ImpactRow = { baseline: number; new: number; deltaAbs: number; deltaPct: number };

@Component({
  selector: 'app-projected-impact',
  standalone: true,
  imports: [CommonModule, ChartModule],
  templateUrl: './projected-impact.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectedImpactComponent implements OnChanges {
  private store = inject(AnalysisStore);

  @Input() selectedInfoSimulation: string = 'Basic Simulation';
  @Input() selectedInfoPeriod: '5 years' | '10 years' | '15 years' | '20 years' = '5 years';
  @Input() selectedCascadeSimulation: string = 'Basic Simulation';

  htCvdView: 'ht' | 'cvd' = 'ht';

  cascadeData: any;
  cascadeOptions: any;

  // Used by template to show "From YYYY to YYYY" dynamically
  baselineYear = 2026;
  projectedYear = 2031;

  htImpactCounts: Record<'undiagnosed' | 'diagnosed' | 'treated' | 'controlled', ImpactRow> = {
    undiagnosed: this.zero(),
    diagnosed: this.zero(),
    treated: this.zero(),
    controlled: this.zero(),
  };

  cvdSummary: Array<{
    id: 'events' | 'patients' | 'deaths';
    title: string;
    iconClass: string;
    baseline: number;
    projected: number;
    deltaPct: number;
    deltaAbs: number;
  }> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedInfoPeriod'] || changes['selectedInfoSimulation'] || changes['selectedCascadeSimulation']) {
      this.recomputeAll();
      return;
    }
    if (!this.cascadeData) this.recomputeAll();
  }

  // src/app/pages/dashboard/projected-impact/projected-impact.component.ts
  // Replace only htNote and cvdNote getters with these versions.

  get htNote(): string {
    const isBaselineSelected =
      (this.selectedInfoSimulation || '').trim().toLowerCase() === 'basic simulation';

    return isBaselineSelected
      ? 'This represents the total number of individuals in each category over the selected time period under the baseline scenario.'
      : 'This represents the difference in the number of individuals in each category compared to the baseline scenario.';
  }

  get cvdNote(): string {
    const isBaselineSelected =
      (this.selectedInfoSimulation || '').trim().toLowerCase() === 'basic simulation';

    return isBaselineSelected
      ? 'This represents the total number of CVD events and deaths over the selected time period under the baseline scenario.'
      : 'This represents the difference in CVD events and deaths compared to the baseline scenario.';
  }


  private recomputeAll(): void {
    const baselineRun = this.store.getBaselineOrFirst();
    if (!baselineRun?.t0) {
      this.resetAll();
      return;
    }

    const scenarioRun = this.store.getRunByTitle(this.selectedInfoSimulation) ?? baselineRun;

    const startYear = 2026;
    const yearsForward = this.parseYearsForward(this.selectedInfoPeriod);
    const desiredTargetYear = startYear + yearsForward;

    const isBaselineSelected =
      (this.selectedInfoSimulation || '').trim().toLowerCase() === 'basic simulation';

    // Rule:
    // - If baseline is selected: compare baseline 2026 vs baseline target year
    // - If simulation is selected: compare baseline target year vs simulation target year
    const baselineYear = this.pickNearestYear(
      baselineRun.t0,
      isBaselineSelected ? startYear : desiredTargetYear
    );

    const scenarioYear = this.pickNearestYear(scenarioRun.t0, desiredTargetYear);

    this.baselineYear = baselineYear;
    this.projectedYear = scenarioYear;

    this.computeComparison(baselineRun.t0, scenarioRun.t0, baselineYear, scenarioYear);
    this.initCascadeChart(baselineRun.t0, scenarioRun.t0, scenarioYear);
  }

  private computeComparison(
    baselineT0: T0ApiResponse,
    scenarioT0: T0ApiResponse,
    baselineYear: number,
    scenarioYear: number
  ): void {
    const undBase = this.store.pick(baselineT0, 'Undiagnosed', baselineYear);
    const diagBase = this.store.pick(baselineT0, 'Diagnosed', baselineYear);
    const trBase = this.store.pick(baselineT0, 'Treated', baselineYear);
    const ctrlBase = this.store.pick(baselineT0, 'Controlled', baselineYear);

    const undNew = this.store.pick(scenarioT0, 'Undiagnosed', scenarioYear);
    const diagNew = this.store.pick(scenarioT0, 'Diagnosed', scenarioYear);
    const trNew = this.store.pick(scenarioT0, 'Treated', scenarioYear);
    const ctrlNew = this.store.pick(scenarioT0, 'Controlled', scenarioYear);

    this.htImpactCounts = {
      undiagnosed: this.deltaRow(undBase, undNew),
      diagnosed: this.deltaRow(diagBase, diagNew),
      treated: this.deltaRow(trBase, trNew),
      controlled: this.deltaRow(ctrlBase, ctrlNew),
    };

    const evBase = this.store.pick(baselineT0, 'CVD_Events', baselineYear);
    const ptBase = this.store.pick(baselineT0, 'CVD_Patients', baselineYear);
    const deBase = this.store.pick(baselineT0, 'CVD_Deaths', baselineYear);

    const evNew = this.store.pick(scenarioT0, 'CVD_Events', scenarioYear);
    const ptNew = this.store.pick(scenarioT0, 'CVD_Patients', scenarioYear);
    const deNew = this.store.pick(scenarioT0, 'CVD_Deaths', scenarioYear);

    this.cvdSummary = [
      this.deltaItem('events', 'CVD Events', 'pi pi-chart-bar', evBase, evNew),
      this.deltaItem('patients', 'CVD Patients', 'pi pi-users', ptBase, ptNew),
      this.deltaItem('deaths', 'CVD Deaths', 'pi pi-exclamation-circle', deBase, deNew),
    ];
  }


  // src/app/pages/dashboard/projected-impact/projected-impact.component.ts
  // Update ONLY initCascadeChart(): increase maintainAspectRatio to false and set a fixed height via aspectRatio.
  // This effectively makes the chart taller (more vertical space).

  private initCascadeChart(baselineT0: T0ApiResponse, scenarioT0: T0ApiResponse, scenarioYear: number): void {
    const labels = ['Undiagnosed', 'Diagnosed', 'Treated', 'Controlled'];

    const baseline = [
      this.toMillions(this.store.pick(baselineT0, 'Undiagnosed', scenarioYear)),
      this.toMillions(this.store.pick(baselineT0, 'Diagnosed', scenarioYear)),
      this.toMillions(this.store.pick(baselineT0, 'Treated', scenarioYear)),
      this.toMillions(this.store.pick(baselineT0, 'Controlled', scenarioYear)),
    ];

    const scenario = [
      this.toMillions(this.store.pick(scenarioT0, 'Undiagnosed', scenarioYear)),
      this.toMillions(this.store.pick(scenarioT0, 'Diagnosed', scenarioYear)),
      this.toMillions(this.store.pick(scenarioT0, 'Treated', scenarioYear)),
      this.toMillions(this.store.pick(scenarioT0, 'Controlled', scenarioYear)),
    ];

    const isBaselineSelected =
      (this.selectedInfoSimulation || '').trim().toLowerCase() === 'basic simulation' ||
      (this.selectedCascadeSimulation || '').trim().toLowerCase() === 'basic simulation';

    const datasets = isBaselineSelected
      ? [{ label: 'Basic Simulation', backgroundColor: '#fec2d6', data: baseline, barThickness: 12 }]
      : [
        { label: 'Basic Simulation', backgroundColor: '#fec2d6', data: baseline, barThickness: 12 },
        {
          label: this.selectedCascadeSimulation || 'Selected Simulation',
          backgroundColor: '#d41c58',
          data: scenario,
          barThickness: 12
        },
      ];

    this.cascadeData = { labels, datasets };

    this.cascadeOptions = {
      responsive: true,
      maintainAspectRatio: false,   // allow container height to control chart height
      aspectRatio: 1,             // smaller ratio => taller chart (try 1.4 or 1.2 if needed)
      plugins: {
        legend: { display: true, labels: { color: '#64748b', font: { size: 11 } } },
        tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(2)}M` } },
      },
      scales: {
        x: { ticks: { color: '#64748b', font: { size: 11 } }, grid: { display: false } },
        y: {
          ticks: { color: '#64748b', font: { size: 11 }, callback: (value: any) => `${value}M` },
          grid: { color: '#e2e8f0', drawBorder: false },
        },
      },
    };
  }

  private deltaRow(baseline: number, projected: number): ImpactRow {
    const deltaAbs = projected - baseline;
    const deltaPct = baseline !== 0 ? Math.round((deltaAbs / baseline) * 100) : 0;
    return { baseline, new: projected, deltaAbs: Math.round(deltaAbs), deltaPct };
  }


  private deltaItem(
    id: 'events' | 'patients' | 'deaths',
    title: string,
    iconClass: string,
    baseline: number,
    projected: number
  ) {
    const deltaAbs = projected - baseline;
    const deltaPct = baseline !== 0 ? Math.round((deltaAbs / baseline) * 100) : 0;

    const trendClass =
      deltaAbs < 0
        ? 'text-emerald-600'
        : deltaAbs > 0
          ? 'text-rose-600'
          : 'text-slate-500';

    return {
      id,
      title,
      // icon gets same trend color behavior
      iconClass: `${iconClass} ${trendClass}`,
      baseline,
      projected,
      deltaPct,
      deltaAbs: Math.round(deltaAbs)
    };
  }

  private pickNearestYear(t0: T0ApiResponse, desired: number): number {
    const years = Array.isArray(t0?.years) ? t0.years.map(Number).filter(Number.isFinite) : [];
    if (!years.length) return desired;
    if (years.includes(desired)) return desired;

    // requirement: fallback to latest available year
    return years[years.length - 1];
  }

  private parseYearsForward(period: string): number {
    const p = String(period).trim();
    if (p.startsWith('20')) return 20;
    if (p.startsWith('15')) return 15;
    if (p.startsWith('10')) return 10;
    return 5;
  }

  private zero(): ImpactRow {
    return { baseline: 0, new: 0, deltaAbs: 0, deltaPct: 0 };
  }

  private resetAll(): void {
    this.baselineYear = 2026;
    this.projectedYear = 2031;

    this.htImpactCounts = {
      undiagnosed: this.zero(),
      diagnosed: this.zero(),
      treated: this.zero(),
      controlled: this.zero(),
    };
    this.cvdSummary = [];
    this.cascadeData = { labels: [], datasets: [] };
    this.cascadeOptions = {};
  }

  private toMillions(n: number): number {
    return Number.isFinite(n) ? Number((n / 1_000_000).toFixed(2)) : 0;
  }

  format(n: number): string {
    return new Intl.NumberFormat('en-US').format(Math.ceil(n));
  }
}